import openai from '../lib/openai';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Exponential backoff configuration for rate limiting
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 8000, // 8 seconds
  backoffMultiplier: 2
};

/**
 * Sleep function for delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay
 */
const getBackoffDelay = (retryCount) => {
  const delay = Math.min(
    RETRY_CONFIG?.baseDelay * Math.pow(RETRY_CONFIG?.backoffMultiplier, retryCount),
    RETRY_CONFIG?.maxDelay
  );
  // Add jitter to prevent thundering herd
  return delay + Math.random() * 1000;
};

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with message, isInternal flag, and retry flag.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { 
      isInternal: true, 
      message: 'Invalid API key or authentication failed. Please check your OpenAI API key.',
      canRetry: false
    };
  } else if (error instanceof PermissionDeniedError) {
    return { 
      isInternal: true, 
      message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.',
      canRetry: false
    };
  } else if (error instanceof RateLimitError) {
    return { 
      isInternal: true, 
      message: 'Rate limit exceeded. Please wait a moment - we\'re automatically retrying your request...',
      canRetry: true
    };
  } else if (error instanceof InternalServerError) {
    return { 
      isInternal: true, 
      message: 'OpenAI service is currently unavailable. We\'ll retry your request shortly...',
      canRetry: true
    };
  } else if (error instanceof APIConnectionError) {
    return { 
      isInternal: true, 
      message: 'Unable to connect to OpenAI service. Retrying connection...',
      canRetry: true
    };
  } else {
    return { 
      isInternal: false, 
      message: error?.message || 'An unexpected error occurred. Please try again.',
      canRetry: false
    };
  }
}

/**
 * Retry wrapper with exponential backoff for API calls
 */
async function withRetry(apiCall, onRetry = null) {
  let lastError = null;
  
  for (let attempt = 0; attempt <= RETRY_CONFIG?.maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      const errorInfo = getErrorMessage(error);
      
      // If error cannot be retried or max retries reached, throw
      if (!errorInfo?.canRetry || attempt === RETRY_CONFIG?.maxRetries) {
        throw error;
      }
      
      // Calculate delay and wait before retrying
      const delay = getBackoffDelay(attempt);
      console.log(`Rate limit hit, retrying in ${Math.round(delay / 1000)}s... (Attempt ${attempt + 1}/${RETRY_CONFIG?.maxRetries})`);
      
      // Notify caller about retry if callback provided
      if (onRetry) {
        onRetry(attempt + 1, RETRY_CONFIG?.maxRetries, Math.round(delay / 1000));
      }
      
      await sleep(delay);
    }
  }
  
  // If we get here, all retries failed
  throw lastError;
}

/**
 * Generates app code based on user description using streaming with retry logic.
 * @param {string} userDescription - User's description of the desired app.
 * @param {Function} onChunk - Callback to handle each streamed chunk.
 * @param {Array} conversationHistory - Previous conversation messages.
 * @param {Function} onRetry - Optional callback for retry notifications.
 */
export async function generateAppCode(userDescription, onChunk, conversationHistory = [], onRetry = null) {
  try {
    const systemPrompt = `You are an expert React app code generator. Your job is to:
1. Understand user requirements for building React applications
2. Generate clean, production-ready React code with Tailwind CSS
3. Follow React best practices (functional components, hooks)
4. Create responsive, accessible UI components
5. Provide complete, working code that can be directly used

When generating code:
- Use React 18 with functional components and hooks
- Style with Tailwind CSS v3.x
- Include proper imports and exports
- Add helpful comments
- Make code modular and reusable
- Ensure accessibility (aria labels, semantic HTML)
- Make it responsive

Format your response with clear code blocks and explanations.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      ...conversationHistory,
      { role: 'user', content: userDescription }
    ];

    await withRetry(async () => {
      const stream = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages,
        stream: true,
        reasoning_effort: 'medium',
        verbosity: 'medium',
        max_completion_tokens: 4000,
      });

      for await (const chunk of stream) {
        const content = chunk?.choices?.[0]?.delta?.content || '';
        if (content) {
          onChunk(content);
        }
      }
    }, onRetry);
    
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.error('AI Service Error:', errorInfo?.message);
    } else {
      console.error('Error in AI code generation:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Analyzes app requirements and suggests implementation approach with retry logic.
 * @param {string} requirements - User's app requirements.
 * @returns {Promise<Object>} Analysis with recommendations.
 */
export async function analyzeAppRequirements(requirements) {
  try {
    return await withRetry(async () => {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are an expert app architect. Analyze user requirements and provide structured recommendations for building React apps.' 
          },
          { 
            role: 'user', 
            content: `Analyze these app requirements and provide recommendations:\n\n${requirements}` 
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'app_analysis',
            schema: {
              type: 'object',
              properties: {
                summary: { type: 'string', description: 'Brief summary of the app' },
                features: { 
                  type: 'array', 
                  items: { type: 'string' },
                  description: 'List of main features'
                },
                components: { 
                  type: 'array', 
                  items: { type: 'string' },
                  description: 'Suggested React components'
                },
                complexity: { 
                  type: 'string', 
                  enum: ['simple', 'medium', 'complex'],
                  description: 'Complexity level'
                },
                estimatedTime: { 
                  type: 'string',
                  description: 'Estimated build time'
                },
                recommendations: {
                  type: 'array',
                  items: { type: 'string' },
                  description: 'Implementation recommendations'
                }
              },
              required: ['summary', 'features', 'components', 'complexity'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'medium',
        verbosity: 'medium',
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    });
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.error('AI Service Error:', errorInfo?.message);
    } else {
      console.error('Error analyzing requirements:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Enhances user prompt for better code generation.
 * @param {string} userPrompt - User's initial prompt.
 * @returns {Promise<string>} Enhanced prompt.
 */
export async function enhancePrompt(userPrompt) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert at refining app requirements. Enhance user prompts to be more specific and actionable for code generation.' 
        },
        { 
          role: 'user', 
          content: `Enhance this app requirement for better code generation:\n\n${userPrompt}` 
        },
      ],
      reasoning_effort: 'minimal',
      verbosity: 'low',
      max_completion_tokens: 500,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error enhancing prompt:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Gets quick suggestions for app ideas with retry logic.
 * @param {string} category - App category (e.g., 'e-commerce', 'social', 'productivity').
 * @returns {Promise<string[]>} Array of app suggestions.
 */
export async function getAppSuggestions(category) {
  try {
    return await withRetry(async () => {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-nano',
        messages: [
          { 
            role: 'system', 
            content: 'Suggest 5 specific app ideas with brief descriptions.' 
          },
          { 
            role: 'user', 
            content: `Suggest 5 ${category} app ideas` 
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'app_suggestions',
            schema: {
              type: 'object',
              properties: {
                suggestions: { 
                  type: 'array', 
                  items: { 
                    type: 'object',
                    properties: {
                      name: { type: 'string' },
                      description: { type: 'string' }
                    },
                    required: ['name', 'description']
                  },
                },
              },
              required: ['suggestions'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'minimal',
        verbosity: 'low',
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return result?.suggestions;
    });
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.error('AI Service Error:', errorInfo?.message);
    } else {
      console.error('Error getting suggestions:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates component suggestions based on current canvas state.
 * @param {Array} currentComponents - Array of components currently on canvas.
 * @param {string} pageContext - Context about the current page being built.
 * @returns {Promise<Object>} Component suggestions with reasoning.
 */
export async function getComponentSuggestions(currentComponents, pageContext = '') {
  try {
    const componentsDescription = currentComponents?.map(c => `${c?.type}: ${c?.name}`)?.join(', ') || 'No components yet';
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: `You are an expert UI/UX designer and React developer. Analyze the current state of a visual builder canvas and suggest the next logical components to add. Consider:
- User experience flow
- Common UI patterns
- Accessibility best practices
- Visual hierarchy
- Component relationships` 
        },
        { 
          role: 'user', 
          content: `Current page: ${pageContext || 'General page'}
Current components: ${componentsDescription}

Suggest 3-5 components that would make sense to add next, with clear reasoning for each.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'component_suggestions',
          schema: {
            type: 'object',
            properties: {
              suggestions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    componentType: { type: 'string', description: 'Type of component (e.g., Button, Input, Card)' },
                    componentName: { type: 'string', description: 'Suggested name for the component' },
                    reasoning: { type: 'string', description: 'Why this component makes sense' },
                    priority: { type: 'string', enum: ['high', 'medium', 'low'], description: 'Importance priority' },
                    placement: { type: 'string', description: 'Where to place it on the canvas' }
                  },
                  required: ['componentType', 'componentName', 'reasoning', 'priority']
                }
              },
              overallRecommendation: { type: 'string', description: 'General advice about the current design' }
            },
            required: ['suggestions', 'overallRecommendation'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'medium',
      verbosity: 'medium',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error getting component suggestions:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Analyzes current layout and provides optimization recommendations.
 * @param {Array} components - Array of components with positions.
 * @param {Object} canvasSize - Canvas dimensions.
 * @returns {Promise<Object>} Layout recommendations.
 */
export async function getLayoutRecommendations(components, canvasSize) {
  try {
    const layoutDescription = components?.map(c => 
      `${c?.type} at position (${c?.position?.x || 0}, ${c?.position?.y || 0})`
    )?.join('; ') || 'No components';

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: `You are an expert UI/UX designer specializing in layout optimization. Analyze component layouts and provide actionable recommendations for:
- Visual hierarchy
- Spacing and alignment
- Responsive design
- Accessibility
- User flow` 
        },
        { 
          role: 'user', 
          content: `Canvas size: ${canvasSize?.width || 1200}x${canvasSize?.height || 600}px
Components layout: ${layoutDescription}

Analyze the layout and provide specific recommendations for improvement.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'layout_recommendations',
          schema: {
            type: 'object',
            properties: {
              overallScore: { type: 'number', minimum: 0, maximum: 10, description: 'Overall layout quality score' },
              strengths: {
                type: 'array',
                items: { type: 'string' },
                description: 'What is working well'
              },
              improvements: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    area: { type: 'string', description: 'Aspect to improve' },
                    suggestion: { type: 'string', description: 'Specific improvement' },
                    impact: { type: 'string', enum: ['high', 'medium', 'low'], description: 'Impact level' }
                  },
                  required: ['area', 'suggestion', 'impact']
                }
              },
              layoutPattern: { type: 'string', description: 'Recommended layout pattern (e.g., grid, flex, hero)' }
            },
            required: ['overallScore', 'improvements', 'layoutPattern'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'high',
      verbosity: 'high',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error getting layout recommendations:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates quick component variations for experimentation.
 * @param {Object} component - The component to generate variations for.
 * @returns {Promise<Array>} Array of component variations.
 */
export async function generateComponentVariations(component) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-nano',
      messages: [
        { 
          role: 'system', 
          content: 'Generate 3 style variations for a UI component. Keep variations practical and visually distinct.' 
        },
        { 
          role: 'user', 
          content: `Generate 3 style variations for: ${component?.type} - ${component?.name}` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'component_variations',
          schema: {
            type: 'object',
            properties: {
              variations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    description: { type: 'string' },
                    styleHints: { type: 'string', description: 'CSS/styling suggestions' }
                  },
                  required: ['name', 'description']
                }
              }
            },
            required: ['variations'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'minimal',
      verbosity: 'low',
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.variations;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating variations:', error);
    }
    throw new Error(errorInfo.message);
  }
}